import React from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import CheckboxGroupOTA from '../../../base/components/forms/ota/CheckboxGroupOTA';
import RadioButtonListOTA from '../../../base/components/forms/ota/RadioButtonListOTA';
import BlockedReasonItem from '~/features/base/components/forms/BlockedReasonItem';
import { deviceStatusOptions } from '~/features/base/constants/filterOptions';
import { FILTER_IDS } from '~/features/devices/constants/filterMenuConstants';
import HeartWrapper from '~/features/devices/components/deviceFilters/HeartWrapper';

interface StatusFiltersSectionProps {
    control: Control<any>;
}

const StatusFiltersSection: React.FC<StatusFiltersSectionProps> = ({ control }) => {
    const blockedValue = useWatch({ control, name: 'updatesActive' });
    const allItem = { id: 'all', label: 'intl-msg:all', value: 'all' };
    const deviceHealthOptions = [
        { id: 'OK', label: 'intl-msg:deviceHealthState.ok', value: 'OK' },
        { id: 'WARN', label: 'intl-msg:deviceHealthState.warning', value: 'WARN' },
        { id: 'ERROR', label: 'intl-msg:deviceHealthState.error', value: 'ERROR' }
    ];
    const blockedOptions = [
        allItem,
        { id: 'true', label: 'intl-msg:notBlocked', value: true },
        { id: 'false', label: 'intl-msg:blocked', value: false }
    ];
    const replacedOptions = [
        allItem,
        { id: 'false', label: 'intl-msg:currentDevices', value: false },
        { id: 'true', label: 'intl-msg:replaced', value: true }
    ];
    const activatedOptions = [
        allItem,
        { id: 'false', label: 'intl-msg:notInCustomerFleet', value: false },
        { id: 'true', label: 'intl-msg:inCustomerFleet', value: true }
    ];
    const testDevicesOptions = [
        allItem,
        { id: 'false', label: 'intl-msg:notTestDevices', value: false },
        { id: 'true', label: 'intl-msg:testDevices', value: true }
    ];
    const productionModeOptions = [
        allItem,
        { id: 'false', label: 'intl-msg:productionModeNotActive', value: false },
        { id: 'true', label: 'intl-msg:productionModeActive', value: true }
    ];
    const ivdMismatchOptions = [
        allItem,
        { id: 'false', label: 'intl-msg:ivdMismatchNo', value: 'false' },
        { id: 'true', label: 'intl-msg:ivdMismatchYes', value: 'true' },
        { id: 'unknown', label: 'intl-msg:ivdMismatchNull', value: 'unknown' }
    ];

    return (
        <div>
            <h4><FormattedMessage id='intl-msg:status' defaultMessage='Status'/></h4>
            <hr/>
            <div id={FILTER_IDS.DEVICE_HEALTH_TYPES}>
                <HeartWrapper>
                    <Controller
                        name='deviceStatusType'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <CheckboxGroupOTA
                                labelId='intl-msg:deviceHealthTypes'
                                options={deviceHealthOptions}
                                value={value}
                                onChange={onChange}
                                multiple
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.DEVICE_STATUS}>
                <HeartWrapper>
                    <Controller
                        name='deviceStatusNames'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <CheckboxGroupOTA
                                labelId='intl-msg:deviceStatus'
                                options={deviceStatusOptions}
                                value={value}
                                onChange={onChange}
                                multiple
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.PROD_MODE}>
                <HeartWrapper>
                <Controller
                    name='productionMode'
                    control={control}
                    defaultValue='all'
                    render={({ field: { value, onChange } }) => (
                        <RadioButtonListOTA
                            labelId='intl-msg:deviceProdMode'
                            options={productionModeOptions}
                            value={value}
                            onChange={onChange}
                        />
                    )}
                />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.IVD_MISMATCH}>
                <HeartWrapper>
                    <Controller
                        name='ivdMismatch'
                        control={control}
                        defaultValue='all'
                        render={({ field: { value, onChange } }) => (
                            <RadioButtonListOTA
                                labelId='intl-msg:ivdMismatch'
                                options={ivdMismatchOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.BLOCKED}>
                <HeartWrapper>
                    <Controller
                        name='updatesActive'
                        control={control}
                        defaultValue='all'
                        render={({ field: { value, onChange } }) => (
                            <>
                                <RadioButtonListOTA
                                    labelId='intl-msg:blocked'
                                    options={blockedOptions}
                                    value={value}
                                    onChange={onChange}
                                />
                                {blockedValue === 'false' && (
                                    <div style={{ width: '50%' }}>
                                        <Controller
                                            name='updateBlockedReason'
                                            control={control}
                                            render={({ field: { value: reasonValue, onChange: onReasonChange } }) => (
                                                <BlockedReasonItem
                                                    onBlockedReasonChange={onReasonChange}
                                                    blockedReason={reasonValue}
                                                />
                                            )}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.REPLACED}>
                <HeartWrapper>
                    <Controller
                        name='replaced'
                        control={control}
                        defaultValue='all'
                        render={({ field: { value, onChange } }) => (
                            <RadioButtonListOTA
                                labelId='intl-msg:replaced'
                                options={replacedOptions}
                                value={value}
                                onChange={onChange}
                                tooltipId={'intl-msg:replacedSelectTooltip'}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.IN_CUSTOMER_FLEET}>
                <HeartWrapper>
                    <Controller
                        name='inCustomerFleet'
                        control={control}
                        defaultValue='all'
                        render={({ field: { value, onChange } }) => (
                            <RadioButtonListOTA
                                labelId='intl-msg:inCustomerFleet'
                                options={activatedOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.TEST_RELEASES_ACTIVE}>
                <HeartWrapper>
                    <Controller
                        name='testReleasesActive'
                        control={control}
                        defaultValue='all'
                        render={({ field: { value, onChange } }) => (
                            <RadioButtonListOTA
                                labelId='intl-msg:testDevices'
                                options={testDevicesOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
        </div>
    );
};

export default StatusFiltersSection;
